import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Radio, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TZ = "Europe/Brussels";
const HOME = "EBCI";

// Offset (in minutes) of the given timeZone vs UTC for a specific instant.
function getTZOffsetMinutes(timeZone, date) {
  const utc = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
  const tz = new Date(date.toLocaleString("en-US", { timeZone }));
  return (tz.getTime() - utc.getTime()) / 60000;
}

// Convert a Brussels-local date + "HH:MM" into a UTC "HHMM" string (DST-aware).
function toUtcHHMM(dateStr, timeStr) {
  if (!dateStr || !timeStr || !/^\d{1,2}:\d{2}$/.test(timeStr)) return null;
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, mi] = timeStr.split(":").map(Number);
  const ref = new Date(Date.UTC(y, mo - 1, d, h, mi));
  const off = getTZOffsetMinutes(TZ, ref);
  const utc = new Date(ref.getTime() - off * 60000);
  return String(utc.getUTCHours()).padStart(2, "0") + String(utc.getUTCMinutes()).padStart(2, "0");
}

function dayOfMonth(dateStr) {
  if (!dateStr) return "";
  return dateStr.split("-")[2] || "";
}

function clean(s) {
  return (s || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

function pad2(n) {
  const v = parseInt(n, 10) || 0;
  return String(v).padStart(2, "0");
}

// Add minutes to a "HHMM" string, wrapping past midnight. Returns "HHMM".
function addMinutesHHMM(hhmm, minutes) {
  const h = parseInt(hhmm.slice(0, 2), 10);
  const m = parseInt(hhmm.slice(2, 4), 10);
  let total = h * 60 + m + minutes;
  total = ((total % 1440) + 1440) % 1440;
  return pad2(Math.floor(total / 60)) + pad2(total % 60);
}

// Build a SITA MVT movement message from a flight leg.
// durationMin (optional): enroute minutes used to compute EA for departures.
export function buildMvtMessage(flight, durationMin = null) {
  const origin = (flight.origin || "").toUpperCase();
  const destination = (flight.destination || "").toUpperCase();
  const isDeparture = origin === HOME; // local flights (EBCI->EBCI) treated as departures
  const reg = clean(flight.registration);
  const flt = clean(flight.flight_number);
  const dd = dayOfMonth(flight.flight_date);
  const crew = parseInt(flight.crew_number, 10) || 0;

  let line2, line3, pax;
  if (isDeparture) {
    const std = toUtcHHMM(flight.flight_date, flight.time);
    const atd = toUtcHHMM(flight.flight_date, flight.atd);
    const base = atd || std; // prefer actual departure, fall back to scheduled
    let ea = "....";
    if (durationMin != null && base) {
      ea = addMinutesHHMM(base, durationMin);
    }
    pax = parseInt(flight.pax_out, 10) || 0;
    line2 = `${flt}/${dd}.${reg}.${origin}`;
    line3 = `AD${std || "...."}/${atd || "...."} EA${ea} ${destination}`;
  } else {
    const sta = toUtcHHMM(flight.flight_date, flight.time) || "....";
    const ata = toUtcHHMM(flight.flight_date, flight.ata) || "....";
    pax = parseInt(flight.pax_in, 10) || 0;
    line2 = `${flt}/${dd}.${reg}.${origin}`;
    line3 = `AA${sta}/${ata} ${destination}`;
  }

  const tob = pax + crew;
  const si = flight.weapon_on_board ? "SI/ WEAPON ON BOARD" : "SI/ NIHIL";

  // CARGO line: use flight cargo info/weight if present, otherwise NIHIL.
  const cargoInfo = (flight.cargo_info || "").trim();
  const cargoKg = parseFloat(flight.cargo_kg) || 0;
  let cargo;
  if (cargoInfo || cargoKg > 0) {
    const parts = [];
    if (cargoInfo) parts.push(cargoInfo.toUpperCase());
    if (cargoKg > 0) parts.push(`${cargoKg}KG`);
    cargo = `CARGO ${parts.join(" ")}`;
  } else {
    cargo = "CARGO NIHIL";
  }

  return ["MVT", line2, line3, `TOB${pad2(tob)}`, `PX${pad2(pax)}`, `CRW${pad2(crew)}`, cargo, si].join("\n");
}

export function MvtButton({ flight, variant = "icon" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);

  const handleOpen = async () => {
    setText(buildMvtMessage(flight));
    setCopied(false);
    setOpen(true);
    // For departures, estimate EA from route distance and patch the message.
    const origin = (flight.origin || "").toUpperCase();
    const destination = (flight.destination || "").toUpperCase();
    if (origin === HOME && destination && destination !== HOME) {
      try {
        const params = new URLSearchParams({
          origin,
          destination,
          aircraft_type: flight.aircraft_type || "",
          mtow: flight.mtow || 0,
        });
        const res = await api.get(`/flights/mvt-eta?${params.toString()}`);
        if (res.data && res.data.duration_min != null) {
          setText(buildMvtMessage(flight, res.data.duration_min));
        }
      } catch (e) {
        // Leave EA as "...." if estimate is unavailable
      }
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("MVT message copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback for non-secure contexts
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      toast.success("MVT message copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleOpen}
          className="text-[#64748b] hover:text-[#d4af37] hover:bg-[#d4af37]/10 h-8 w-8"
          title="Generate MVT message"
          data-testid={`mvt-button-${flight.id}`}
        >
          <Radio size={16} />
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpen}
          className="flex items-center gap-2 text-[#0f172a] border-[#d4af37] hover:bg-[#d4af37] hover:text-white"
          data-testid={`mvt-button-${flight.id}`}
        >
          <Radio size={14} />
          MVT
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white dark:bg-[#1e293b]" data-testid="mvt-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a] dark:text-white flex items-center gap-2">
              <Radio size={22} className="text-[#d4af37]" />
              MVT Message
              <span className="text-sm font-normal text-[#64748b] dark:text-gray-400">
                {flight.flight_number} · {(flight.origin || "").toUpperCase() === HOME ? "DEP" : "ARR"}
              </span>
            </DialogTitle>
          </DialogHeader>

          <p className="text-xs text-[#64748b] dark:text-gray-400 -mt-2">
            Times shown in UTC (Zulu). Edit the message below before copying.
          </p>

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            className="font-mono text-sm bg-[#f8fafc] dark:bg-[#0f172a] text-[#0f172a] dark:text-white tracking-wide"
            data-testid="mvt-textarea"
          />

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setText(buildMvtMessage(flight))}
              data-testid="mvt-reset-button"
            >
              Reset
            </Button>
            <Button
              onClick={handleCopy}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] flex items-center gap-2"
              data-testid="mvt-copy-button"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
